import React from 'react';
import { cn } from '@/utils/cn';
import { AppImage } from '@/components/Images';
import { TextContent } from '@/components/Text';
import { CERTIFICATE_LABELS } from '@/constants/labels';

export interface CertificationCertificateProps {
  /**
   * Name of the person receiving the certificate
   */
  recipientName: string;
  /**
   * Name of the certification completed
   */
  certificationName: string;
  /**
   * Date when the certification was completed
   */
  completionDate: string;
  /**
   * Team or organization issuing the certificate
   */
  issuingTeam?: string;
  /**
   * Additional CSS classes to apply to the certificate container
   */
  className?: string;
  /**
   * URL path to the certification badge/medal image
   */
  badgeImageSrc?: string;
  /**
   * URL path to the company/organization logo
   */
  logoImageSrc?: string;
  /**
   * Loading state for the certificate component
   */
  isLoading?: boolean;
  /**
   * Printing state for the certificate component
   */
  isPrinting?: boolean;
}

/**
 * A certificate of achievement component for displaying completed certifications
 * with recipient name, certification details, and official branding.
 */
export const CertificationCertificate: React.FC<CertificationCertificateProps> = ({
  recipientName,
  certificationName,
  completionDate,
  issuingTeam,
  className,
  badgeImageSrc = '/images/certificate-graphic-emblem.png',
  logoImageSrc = '/images/JJ_IM_Logo_SingleLine_Red_RGB.svg',
  isLoading = false,
  isPrinting = false,
}) => {
  return (
    <div
      className={cn(
        'bg-neutral-white',
        'border border-solid border-brand-primary',
        'flex flex-col items-start',
        'p-4',
        'rounded-lg',
        'w-full max-w-4xl',
        className,
      )}
    >
      <div
        className={cn(
          'bg-neutral-white',
          'border-[5px] border-solid border-brand-primary',
          'flex flex-col items-center',
          'p-10',
          'rounded-lg',
          'w-full',
        )}
      >
        <div className="flex w-full flex-col items-center gap-4">
          {/* Title */}
          <div className="flex flex-col justify-center text-center">
            <h1
              className={cn('typography-page-title font-display font-bold text-neutral-black', {
                'typography-section-title': isPrinting,
              })}
            >
              {CERTIFICATE_LABELS.CERTIFICATE_TITLE}
            </h1>
          </div>

          {/* Recipient Section */}
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex flex-col justify-center">
              <p className="font-text text-xl font-normal text-neutral-gray-700">
                {CERTIFICATE_LABELS.CERTIFICATE_USER_PREFIX}
              </p>
            </div>
            <div className="flex flex-col justify-center">
              <TextContent
                isLoading={isLoading}
                label={recipientName || ' '}
                level={2}
                type="headline"
                variant="pageTitle"
              />
            </div>
            <div className="flex max-w-lg flex-col justify-center text-center">
              <p className="mb-0 font-text text-xl font-normal text-neutral-black">
                {CERTIFICATE_LABELS.CERTIFICATE_USER_SUFFIX}
              </p>
              <TextContent
                isLoading={isLoading}
                label={certificationName || ' '}
                type="paragraph"
                variant="bodyLarge"
                color="neutral-black"
                loadingClassName="mx-auto"
                weight="bold"
              />
            </div>
          </div>

          {/* Badge/Medal Image */}
          <div className="relative flex-shrink-0">
            <AppImage
              alt="Certification badge"
              className="object-fit h-full w-full rounded-full"
              isPrinting={isPrinting}
              src={badgeImageSrc}
              height={140}
              width={140}
            />
          </div>

          {/* Logo and Footer */}
          <div className="flex flex-col items-center gap-6">
            <div className="h-12 w-60 flex-shrink-0">
              <AppImage
                alt="Company logo"
                className="h-full w-full object-contain"
                isPrinting={isPrinting}
                src={logoImageSrc}
                height={48}
                width={240}
              />
            </div>
            <div className="flex max-w-lg flex-col justify-center text-center">
              <TextContent
                isLoading={isLoading}
                label={issuingTeam || ' '}
                type="paragraph"
                variant="bodyLarge"
                color="neutral-gray-700"
                loadingClassName="mx-auto mb-0.5 w-[400px]"
                className="mb-0.5"
                weight="regular"
              />
              <TextContent
                isLoading={isLoading}
                label={completionDate || ' '}
                type="paragraph"
                variant="bodyLarge"
                color="neutral-gray-700"
                loadingClassName="mx-auto w-[150px]"
                weight="regular"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
